import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users } from "lucide-react";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/modals/Modal";

export function JoinSchoolModal({ open, onClose }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [inviteCode, setInviteCode] = useState("");
  const [joining, setJoining] = useState(false);

  const handleClose = () => {
    setInviteCode("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = inviteCode.trim();
    if (!code) return;

    setJoining(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ inviteCode: code }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || "Failed to join school");

      toast.success(`Joined ${body.school?.name || "school"} successfully!`);
      handleClose();
      navigate(`/schools/${body.school?._id || ""}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setJoining(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Join a School"
      subtitle="Enter the invite code shared by your school admin."
      icon={<Users size={16} className="text-primary" />}
      preventClose={joining}
    >
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">
            Invite Code <span className="text-destructive/80 font-normal">*</span>
          </Label>
          <Input
            placeholder="e.g. aB3kX9mQ2z"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            required
            className="h-10 font-mono tracking-wider"
            autoComplete="off"
            spellCheck={false}
          />
          <p className="text-xs text-muted-foreground">Ask your school admin for the invite code.</p>
        </div>

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={joining || !inviteCode.trim()} className="flex-1">
            {joining ? "Joining..." : "Join School"}
          </Button>
          <Button type="button" variant="outline" onClick={handleClose} disabled={joining}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
